// Product Form JavaScript - Handles variant selection and real-time updates
class ProductForm {
  constructor(productElement) {
    this.productElement = productElement;
    this.productInfo = this.productElement.querySelector('product-info');
    if (!this.productElement) return;

    this.form = this.productElement.querySelector('product-form form[data-type="add-to-cart-form"]');
    this.submitButton = this.form?.querySelector('[type="submit"]');
    this.submitButtonText = this.submitButton?.querySelector('.button-text');
    this.hiddenVariantInput = this.form?.querySelector('input[name="id"]');
    this.loadingSpinner = this.submitButton?.querySelector('.loading-overlay__spinner');
    this.errorWrapper = this.form?.querySelector('.product-form__error-message-wrapper');
    this.errorMessage = this.form?.querySelector('.product-form__error-message');

    this.priceContainer = this.productElement.querySelector('#ProductPrice');
    this.availabilityContainer = this.productElement.querySelector('#ProductAvailability');

    // Image gallery elements
    this.thumbnails = this.productElement.querySelectorAll('.product-thumbnail-vertical');
    this.variantImages = this.getVariantImages();

    this.variantData = this.getVariantData();
    this.init();
  }

  init() {
    // Set up variant change listeners
    this.attachVariantChangeListeners();

    // Set up form submission
    if (this.form) {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    // Initialize with current variant from URL or default
    this.initializeVariant();
  }

  getVariantData() {
    const variantsScript =
      this.productElement.querySelector('[data-product-variants]') ||
      document.querySelector('[data-product-variants]');

    if (!variantsScript) return [];

    try {
      return JSON.parse(variantsScript.textContent);
    } catch (e) {
      console.error('Error parsing variant data:', e);
      return [];
    }
  }

  getVariantImages() {
    const variantImagesScript =
      this.productElement.querySelector('[data-variant-images]') ||
      document.querySelector('[data-variant-images]');

    if (!variantImagesScript) return {};

    try {
      return JSON.parse(variantImagesScript.textContent);
    } catch (e) {
      console.error('Error parsing variant images:', e);
      return {};
    }
  }

  attachVariantChangeListeners() {
    const optionInputs = this.productElement.querySelectorAll('.optionValueSelectors input[type="radio"]');
    optionInputs.forEach(input => {
      input.addEventListener('change', this.onVariantChange.bind(this));
    });
  }

  findThumbnailByImageId(imageId) {
    return Array.from(this.thumbnails).find(
      thumb => thumb.dataset.mediaId === imageId.toString()
    );
  }

  getGalleryApi() {
    const gallery = this.productElement.querySelector('[data-product-gallery]');
    if (!gallery) return null;
    return gallery.galleryApi || null;
  }

  initializeVariant() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');

    if (variantId) {
      this.selectVariantById(variantId);
    } else {
      // Ensure valid selection on page load
      this.ensureValidSelection();
    }
  }

  selectVariantById(variantId) {
    const variant = this.variantData.find(v => v.id.toString() === variantId.toString());
    if (!variant) return;

    // Select the appropriate options for this variant
    variant.options.forEach((optionValue, index) => {
      const fieldsets = this.productElement.querySelectorAll('.optionValueSelectors fieldset');
      if (fieldsets[index]) {
        const input = fieldsets[index].querySelector(`input[value="${optionValue}"]`);
        if (input) {
          input.checked = true;
        }
      }
    });

    this.updateVariantSelection();
  }

  ensureValidSelection() {
    const fieldsets = this.productElement.querySelectorAll('.optionValueSelectors fieldset');
    let needsUpdate = false;

    fieldsets.forEach(fieldset => {
      const checkedInput = fieldset.querySelector('input:checked');
      const availableInput = fieldset.querySelector('input:not([disabled])');

      // If no option selected or selected option is disabled, select first available
      if (!checkedInput || checkedInput.disabled) {
        if (checkedInput && checkedInput.disabled) {
          checkedInput.checked = false;
        }

        if (availableInput) {
          availableInput.checked = true;
          needsUpdate = true;
        }
      }
    });

    if (needsUpdate) {
      this.updateVariantSelection();
    }
  }

  async onVariantChange(event) {
    const optionValueSelectors = this.productElement.querySelector('.optionValueSelectors');
    const sectionId = this.productElement.dataset.sectionId;
    const productUrl = event.target.dataset.productUrl || this.productElement.dataset.productUrl;

    // Get selected option values
    const selectedOptionValues = Array.from(
      optionValueSelectors.querySelectorAll('input:checked')
    ).map(input => input.dataset.optionValueId);

    const params = selectedOptionValues.length > 0
      ? `&option_values=${selectedOptionValues.join(',')}`
      : '';

    try {
      // Fetch updated section with new availability
      const response = await fetch(`${productUrl}?section_id=${sectionId}${params}`);
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Update option selectors with new availability
      const newOptionsElement = html.querySelector('.optionValueSelectors');
      if (newOptionsElement) {
        optionValueSelectors.innerHTML = newOptionsElement.innerHTML;

        // Re-attach event listeners
        this.attachVariantChangeListeners();

        // Ensure valid selection after update
        this.ensureValidSelectionAfterUpdate();
      }

      // Update availability container from server response (re-renders current_variant logic)
      const newAvailability = html.querySelector('#ProductAvailability');
      if (newAvailability && this.availabilityContainer) {
        this.availabilityContainer.innerHTML = newAvailability.innerHTML;
      }

      // Update variant selection and UI
      this.updateVariantSelection();

    } catch (error) {
      console.error('Error updating variant:', error);
    } finally {
      // Always clear loading states after variant change completes
      const loadingTriggers = this.productElement.querySelectorAll('.variant-dropdown__summary--loading');
      loadingTriggers.forEach(trigger => trigger.classList.remove('variant-dropdown__summary--loading'));
    }
  }

  ensureValidSelectionAfterUpdate() {
    const fieldsets = this.productElement.querySelectorAll('.optionValueSelectors fieldset');

    fieldsets.forEach(fieldset => {
      const checkedInput = fieldset.querySelector('input:checked');
      const availableInput = fieldset.querySelector('input:not([disabled])');

      if (checkedInput && checkedInput.disabled) {
        checkedInput.checked = false;
        if (availableInput) {
          availableInput.checked = true;
        }
      }
    });
  }

  updateVariantSelection() {
    // Get currently selected variant
    const selectedVariant = this.getSelectedVariant();

    if (selectedVariant) {
      // Update hidden input
      if (this.hiddenVariantInput) {
        this.hiddenVariantInput.value = selectedVariant.id;
        this.hiddenVariantInput.disabled = !selectedVariant.available;
      }

      // Update URL
      this.updateURL(selectedVariant.id);

      // Update price
      this.updatePrice(selectedVariant);

      // Update availability display
      this.updateAvailability(selectedVariant);

      // Update button state
      this.updateButtonState(selectedVariant);

      // Update product image for variant
      this.updateVariantImage(selectedVariant);

      // Update variant data script
      this.updateVariantDataScript(selectedVariant);
    }
  }

  updateVariantImage(variant) {
    const galleryApi = this.getGalleryApi();

    // Check if this variant has a specific image
    const variantImage = this.variantImages[variant.id];

    if (variantImage) {
      if (galleryApi?.goToSlideByMediaId) {
        galleryApi.goToSlideByMediaId(variantImage.id);
        return;
      }

      const correspondingThumbnail = this.findThumbnailByImageId(variantImage.id);
      if (correspondingThumbnail) {
        correspondingThumbnail.click();
      }
      return;
    }

    if (variant.featured_image && galleryApi?.goToSlideByMediaId) {
      const featuredImageId = variant.featured_image.id;
      if (featuredImageId) {
        galleryApi.goToSlideByMediaId(featuredImageId);
      }
    }
  }

  getSelectedVariant() {
    // Check for single variant product
    const defaultVariantInput = this.productElement.querySelector('input[id$="-default-variant"]');
    if (defaultVariantInput) {
      const variantId = defaultVariantInput.dataset.variantId;
      return this.variantData.find(v => v.id.toString() === variantId);
    }

    // Multi-variant product - get selected options
    const fieldsets = this.productElement.querySelectorAll('.optionValueSelectors fieldset');
    const selectedOptions = [];

    fieldsets.forEach(fieldset => {
      const checkedInput = fieldset.querySelector('input:checked');
      if (checkedInput) {
        selectedOptions.push(checkedInput.value);
      }
    });

    // Find matching variant
    return this.variantData.find(variant => {
      return JSON.stringify(variant.options) === JSON.stringify(selectedOptions);
    });
  }

  updateURL(variantId) {
    if (this.productElement.dataset.updateUrl === 'false') return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  updatePrice(variant) {
    if (!this.priceContainer) return;

    let priceHTML = `<span class="price">${this.formatMoney(variant.price)}</span>`;

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceHTML += ` <span class="compare-price">${this.formatMoney(variant.compare_at_price)}</span>`;
    }

    this.priceContainer.innerHTML = priceHTML;
  }

  updateAvailability(variant) {
    if (!this.availabilityContainer) return;

    if (variant.available) {
      this.availabilityContainer.innerHTML = '';
    } else {
      this.availabilityContainer.innerHTML = '<p class="flex flex-row items-center gap-3xs text-xs"><span class="block text-red">●</span> Out of stock</p>';
    }
  }

  updateButtonState(variant) {
    if (!this.submitButton || !this.submitButtonText) return;

    /*
     * Labels are written into the button via Liquid using the existing
     * `product.add_to_cart` / `product.sold_out` translation keys. Fall back
     * to English defaults so the JS keeps working if a Liquid template
     * forgets to set the data attributes.
     */
    const availableText = this.submitButton.dataset.textAvailable || 'Add to basket';
    const unavailableText = this.submitButton.dataset.textUnavailable || 'Sold out';

    if (variant.available) {
      this.submitButton.disabled = false;
      this.submitButtonText.textContent = availableText;
    } else {
      this.submitButton.disabled = true;
      this.submitButtonText.textContent = unavailableText;
    }
  }

  updateVariantDataScript(variant) {
    const variantScript =
      this.productElement.querySelector('[data-selected-variant]') ||
      document.querySelector('[data-selected-variant]');

    if (variantScript) {
      variantScript.textContent = JSON.stringify(variant);
    }
  }

  formatMoney(cents) {
    // Simple money formatting - you may want to use Shopify's money format
    return '$' + (cents / 100).toFixed(2);
  }

  async onFormSubmit(event) {
    event.preventDefault();

    // Prevent double submission
    if (this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') {
      return;
    }

    // Show loading state
    this.setLoadingState(true);

    // Prepare form data
    const formData = new FormData(this.form);

    try {
      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        this.onAddToCartSuccess(result);
      } else {
        const error = await response.json();
        this.onAddToCartError(error);
      }
    } catch (error) {
      console.error('Cart submission error:', error);
      this.onAddToCartError({ message: 'An error occurred. Please try again.' });
    } finally {
      this.setLoadingState(false);
    }
  }

  setLoadingState(loading) {
    if (loading) {
      this.submitButton?.setAttribute('aria-disabled', 'true');
      this.submitButton?.classList.add('loading');
      this.loadingSpinner?.classList.remove('hidden');
    } else {
      this.submitButton?.removeAttribute('aria-disabled');
      this.submitButton?.classList.remove('loading');
      this.loadingSpinner?.classList.add('hidden');
    }
  }

  onAddToCartSuccess(result) {
    // Hide error messages
    if (this.errorWrapper) {
      this.errorWrapper.setAttribute('hidden', '');
    }

    // Dispatch custom event for cart update
    document.dispatchEvent(new CustomEvent('cart:add', {
      detail: { item: result }
    }));

    // You can add a success notification here
    console.log('Product added to cart:', result);
  }

  onAddToCartError(error) {
    if (this.errorWrapper && this.errorMessage) {
      this.errorMessage.textContent = error.description || error.message || 'An error occurred';
      this.errorWrapper.removeAttribute('hidden');
    }

    if (error.description?.includes('sold out')) {
      this.submitButton?.setAttribute('aria-disabled', 'true');
      if (this.submitButtonText) {
        this.submitButtonText.textContent = this.submitButton?.dataset.textUnavailable || 'Sold out';
      }
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const mainProduct = document.querySelector('#product');
  if (mainProduct) {
    new ProductForm(mainProduct);
  }

  const drawerProducts = document.querySelectorAll('.quick-shop-drawer');
  drawerProducts.forEach(drawer => {
    new ProductForm(drawer);
  });
});

// Make class available globally if needed
window.ProductForm = ProductForm;
